/**
 * Appskal: sessionsindlæsning, routing, bundnavigation og aktivitetslogning.
 *
 * Menupunkter vises kun for de roller, der har adgang — men serveren
 * håndhæver altid rettighederne uafhængigt af brugerfladen (§4).
 */

import { useEffect, useState } from 'react';
import { antal, api, ApiError, setCsrfToken } from './lib/api.js';
import { Link, matchPath, useLocation } from './lib/router.js';
import { Field, Notice, Skeleton } from './components/ui.js';
import { Icon, type IconName } from './components/icons.js';
import { HomePage, MyTeamsPage, NoAccessPage, TeamDetailPage, VolunteersPage } from './pages/member.js';
import { CalendarPage, MessagesPage } from './pages/calendar-messages.js';
import { PrivacyPage, MemberLoginPage, ProfilePage } from './pages/profile.js';
import {
  WorkspaceNav,
  EventsPage,
  WorkspacePage,
  WorkspaceTeamDetailPage,
  WorkspaceTeamsPage,
  InstructorsPage,
} from './pages/workspace.js';
import { ConventusPage, RolesPage, StatisticsPage } from './pages/board.js';
import { SeasonsPage } from './pages/seasons.js';

interface SessionUser {
  id: string;
  name: string;
  email: string;
  role: string;
  departments: string[];
  hasWorkspace: boolean;
  hasFamily: boolean;
}

interface Session {
  authenticated: boolean;
  csrfToken?: string;
  user?: SessionUser;
  brand: { name: string; fullName: string; tagline: string };
  /** Serveren bestemmer loginmetoden; klienten gætter ikke. */
  authProvider?: 'email' | 'chatgpt-sites' | 'local-dev';
  /** Demo med opdigtede data. Vises tydeligt, så ingen tager fejl. */
  demo?: boolean;
}

export function App(): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [failed, setFailed] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    api
      .get<Session>('/api/session')
      .then((data) => {
        if (data.csrfToken) setCsrfToken(data.csrfToken);
        setSession(data);
      })
      .catch(() => setFailed(true));
  }, []);

  // Registrerer sidevisningen, når stien ændrer sig. Kun for loggede
  // brugere, og kun for de sider, serveren accepterer.
  useEffect(() => {
    if (!session?.authenticated) return;
    void api.post('/api/activity', { path: pathname }).catch(() => {
      // Aktivitetslogning må aldrig forstyrre brugeren.
    });
  }, [pathname, session?.authenticated]);

  if (failed) {
    return (
      <main className="page">
        <Notice kind="error">
          Der er ingen forbindelse til serveren. Prøv at genindlæse siden.
        </Notice>
      </main>
    );
  }

  if (!session) {
    return (
      <main className="page">
        <div className="card">
          <Skeleton lines={5} />
          <span className="sr-only">Indlæser Skals GF …</span>
        </div>
      </main>
    );
  }

  if (!session.authenticated) {
    return (
      <PublicLanding
        brand={session.brand}
        provider={session.authProvider ?? 'email'}
        demo={session.demo ?? false}
      />
    );
  }

  const user = session.user!;
  const isWorkspace = pathname.startsWith('/arbejdsrum');

  return (
    <div className="app">
      <a className="skip-link" href="#indhold">
        Gå til indhold
      </a>
      <AppHeader pathname={pathname} />
      {session.demo ? <DemoBanner /> : null}
      <main
        id="indhold"
        className={isWorkspace ? 'page page--wide' : 'page'}
        tabIndex={-1}
      >
        {isWorkspace ? (
          <div className="workspace">
            <WorkspaceNav role={user.role} />
            <div>
              <Routes pathname={pathname} user={user} />
            </div>
          </div>
        ) : (
          <Routes pathname={pathname} user={user} />
        )}
      </main>
      <BottomNav pathname={pathname} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Routing                                                             */
/* ------------------------------------------------------------------ */

function Routes({ pathname, user }: { pathname: string; user: SessionUser }): JSX.Element {
  const teamMatch = matchPath('/hold/:slug', pathname);
  if (teamMatch) return <TeamDetailPage slug={teamMatch.slug} />;

  const workspaceTeamMatch = matchPath('/arbejdsrum/hold/:slug', pathname);
  if (workspaceTeamMatch) {
    if (!user.hasWorkspace) return <NoAccessPage />;
    return <WorkspaceTeamDetailPage slug={workspaceTeamMatch.slug} />;
  }

  switch (pathname) {
    case '/':
      return <HomePage />;
    case '/mine-hold':
      return <MyTeamsPage hasWorkspace={user.hasWorkspace} />;
    case '/kalender':
      return <CalendarPage />;
    case '/beskeder':
      return <MessagesPage />;
    case '/mere':
      return <MorePage user={user} />;
    case '/profil':
      return <ProfilePage user={user} />;
    case '/medlemslogin':
      return <MemberLoginPage userName={user.name} />;
    case '/frivillige':
      return <VolunteersPage />;
    case '/arbejdsrum':
      return user.hasWorkspace ? <WorkspacePage /> : <NoAccessPage />;
    case '/arbejdsrum/hold':
      return user.hasWorkspace ? <WorkspaceTeamsPage /> : <NoAccessPage />;
    case '/arbejdsrum/saeson':
      // Alle med arbejdsrumsadgang må SE sæsonen — kun bestyrelsen må ændre
      // den. Instruktøren skal kunne slå ferieplanen op uden at spørge.
      return user.hasWorkspace ? (
        <SeasonsPage isBoard={user.role === 'board' || user.role === 'admin'} />
      ) : (
        <NoAccessPage />
      );
    case '/arbejdsrum/arrangementer':
      // Alle med arbejdsrumsadgang. Hvem der må oprette til HVILKEN
      // målgruppe afgøres på serveren med de samme regler som beskeder —
      // en instruktør kan lave et arrangement for sit eget hold, men ikke
      // for foreningen.
      return user.hasWorkspace ? <EventsPage /> : <NoAccessPage />;
    case '/arbejdsrum/instruktoerer':
      // Udvalg og opefter. Serveren afgør, HVILKE hold der kan ses og rettes
      // — siden viser bare det, den får.
      return user.hasWorkspace &&
        (isBoardOrAdmin(user.role) || user.role === 'committee') ? (
        <InstructorsPage />
      ) : (
        <NoAccessPage />
      );
    case '/arbejdsrum/roller':
      // Bestyrelsen må se og rette roller — men kun tildele instruktør og
      // medlem. Grænsen håndhæves på serveren; formularen viser den bare.
      return isBoardOrAdmin(user.role) ? (
        <RolesPage currentUserId={user.id} role={user.role} />
      ) : (
        <NoAccessPage />
      );
    case '/arbejdsrum/statistik':
      return isBoardOrAdmin(user.role) ? <StatisticsPage /> : <NoAccessPage />;
    case '/arbejdsrum/conventus':
      // Kun administrator. Serveren afviser uanset hvad der står her.
      return user.role === 'admin' ? <ConventusPage /> : <NoAccessPage />;
    case '/privatliv':
      // Oplysningspligten. Tilgængelig for alle med en konto — også dem, der
      // ikke har forbundet deres profil endnu.
      return <PrivacyPage />;
    case '/ingen-adgang':
      return <NoAccessPage />;
    default:
      return (
        <div className="stack">
          <div className="page-head">
            <h1>Siden findes ikke</h1>
            <p className="page-head__sub">Adressen «{pathname}» findes ikke i appen.</p>
          </div>
          <div>
            <Link className="btn" to="/">
              <Icon name="home" size={19} />
              Til forsiden
            </Link>
          </div>
        </div>
      );
  }
}

/*
 * Siderne bag «Mere». Listen står ét sted, fordi den bruges to gange: til
 * at markere punktet som aktivt, og til at bygge selve siden.
 */
const MORE_PATHS = ['/mere', '/profil', '/frivillige', '/medlemslogin', '/arbejdsrum', '/privatliv'];

function isUnderMore(pathname: string): boolean {
  return MORE_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * «Mere» — de sider, der ikke bruges dagligt.
 *
 * Siden er en liste og ikke et gitter af kort. Det er fire punkter, man
 * læser ét ad gangen, ikke fire ting man sammenligner.
 *
 * Administration står kun her for konti, der har adgang. At skjule punktet
 * er en bekvemmelighed og ikke en sikkerhedsforanstaltning: serveren
 * afviser stadig et kald til /api/workspace fra en konto uden adgang,
 * uanset hvad brugerfladen viser.
 */
function MorePage({ user }: { user: SessionUser }): JSX.Element {
  const rows: Array<{ to: string; title: string; sub: string; icon: IconName }> = [
    { to: '/profil', title: 'Profil', sub: 'Konto, familie og notifikationer', icon: 'profile' },
    { to: '/frivillige', title: 'Frivillige opgaver', sub: 'Se og tilmeld dig opgaver', icon: 'volunteer' },
  ];
  if (user.hasWorkspace) {
    rows.push({
      to: '/arbejdsrum',
      title: 'Administration',
      sub: 'Hold, sæson, beskeder og roller',
      icon: 'settings',
    });
  }

  return (
    <div className="stack">
      <div className="page-head">
        <h1>Mere</h1>
        <p className="page-head__sub">Det, du ikke bruger hver dag</p>
      </div>
      <nav className="rowlist" aria-label="Flere sider">
        {rows.map((row) => (
          <Link className="rowlist__row" key={row.to} to={row.to}>
            <span className="rowlist__icon">
              <Icon name={row.icon} size={20} />
            </span>
            <span className="rowlist__text">
              <span className="rowlist__title">{row.title}</span>
              <span className="rowlist__sub">{row.sub}</span>
            </span>
            <Icon name="forward" size={18} />
          </Link>
        ))}
      </nav>
    </div>
  );
}

/** Bestyrelsens områder — administrator er med, rollen er bestyrelse plus. */
function isBoardOrAdmin(role: string): boolean {
  return role === 'board' || role === 'admin';
}

/* ------------------------------------------------------------------ */
/* Bundnavigation                                                      */
/* ------------------------------------------------------------------ */

/*
 * Bundnavigationens fem punkter. Hvert har et pictogram fra appens eget
 * ikonsæt — ikke en emoji. En emoji tegnes af styresystemet og ser derfor
 * forskellig ud på iPhone, Android og Windows, den kan ikke få foreningens
 * farve, og den kan ikke skifte til en aktiv variant.
 */
type NavItem = {
  to: string;
  label: string;
  icon: IconName;
};

/*
 * FEM PUNKTER, ikke seks.
 *
 * Seks punkter på en telefon giver 63 px hver — mindre end en tommelfinger
 * er bred. Designguiden siger højst fem, og de fem er valgt efter, hvad
 * folk faktisk åbner appen for: hvornår er der træning, hvad er der sket,
 * og hvad går mit barn til.
 *
 * Profil og Administration er flyttet ind under «Mere». Ingen af dem
 * bruges dagligt, og begge er stadig ét tryk fra forsiden — Profil har
 * desuden sin egen genvej oppe i headeren.
 */
const NAV_ITEMS: Array<NavItem> = [
  { to: '/', label: 'Hjem', icon: 'home' },
  { to: '/mine-hold', label: 'Mine hold', icon: 'teams' },
  { to: '/kalender', label: 'Kalender', icon: 'calendar' },
  { to: '/beskeder', label: 'Beskeder', icon: 'message' },
  { to: '/mere', label: 'Mere', icon: 'settings' },
];

/*
 * Administration står i bundmenuen, men KUN for konti, der har adgang.
 *
 * At skjule punktet er en bekvemmelighed, ikke en sikkerhedsforanstaltning:
 * serveren afviser stadig et kald til /api/workspace fra en konto uden
 * adgang, uanset hvad brugerfladen viser. Det ene må aldrig erstatte det
 * andet.
 */
function BottomNav({ pathname }: { pathname: string }): JSX.Element {
  const [unread, setUnread] = useState(0);
  const items = NAV_ITEMS;

  useEffect(() => {
    api
      .get<{ unreadCount: number }>('/api/home')
      .then((data) => setUnread(data.unreadCount))
      .catch(() => setUnread(0));
  }, [pathname]);

  return (
    <nav className="bottom-nav" aria-label="Hovednavigation">
      {items.map((item) => {
        /*
         * Administration dækker flere undersider (/arbejdsrum/hold,
         * /arbejdsrum/saeson …). Punktet skal blive ved med at være markeret
         * som det aktive, mens man er inde i dem — ellers ser det ud, som om
         * man har forladt afsnittet.
         */
        /*
         * «Mere» dækker de sider, der ligger bag punktet — profil,
         * frivillige og hele administrationen. Punktet skal blive ved med
         * at være markeret, mens man er inde i dem; ellers ser det ud, som
         * om man har forladt afsnittet.
         */
        const current =
          item.to === '/mere' ? isUnderMore(pathname) : pathname === item.to;
        return (
        <Link
          key={item.to}
          to={item.to}
          aria-current={current ? 'page' : undefined}
        >
          <span className="bottom-nav__icon">
            <Icon name={item.icon} size={23} active={current} />
          </span>
          <span>{item.label}</span>
          {item.to === '/beskeder' && unread > 0 ? (
            <span className="bottom-nav__dot" aria-hidden="true">
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
          {item.to === '/beskeder' && unread > 0 ? (
            <span className="sr-only">{antal(unread, 'ulæst besked', 'ulæste beskeder')}</span>
          ) : null}
        </Link>
        );
      })}
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* Offentlig forside                                                   */
/* ------------------------------------------------------------------ */

/** Det, en ny bruger skal kunne se på fem sekunder. */
const LANDING_POINTS: Array<{ icon: IconName; text: string }> = [
  { icon: 'teams', text: 'Se familiens hold med ugedag, tid og sted' },
  { icon: 'bell', text: 'Få besked med det samme, hvis en træning aflyses eller flyttes' },
  { icon: 'calendar', text: 'Se kommende træninger og meld afbud' },
  { icon: 'message', text: 'Læs beskeder fra foreningen, afdelingen og holdet — og svar afsenderen' },
  { icon: 'volunteer', text: 'Tilmeld dig frivillige opgaver' },
];

function PublicLanding({
  brand,
  provider,
  demo,
}: {
  brand: Session['brand'];
  provider: NonNullable<Session['authProvider']>;
  demo: boolean;
}): JSX.Element {
  return (
    <div className="app">
      {demo ? <DemoBanner /> : null}
      <main className="page stack" id="indhold">
        <header className="hero hero--logo">
          {/*
            Logoet står kun på forsiden, hvor det gør en forskel: det er her,
            man skal kunne se med det samme, at man er landet det rigtige
            sted. Inde i appen ville det bare tage plads fra indholdet på en
            telefonskærm. Filen ligger i public/ og serveres fra roden.
            alt="" fordi foreningens navn står som tekst lige ved siden af —
            en skærmlæser skal ikke sige det to gange.
          */}
          <img className="hero__logo" src="/sgf-logo.png" alt="" width={64} height={64} />
          <div>
            <p className="muted small">{brand.fullName}</p>
            <h1>{brand.name}</h1>
            <p className="hero__tagline">{brand.tagline}</p>
          </div>
        </header>

        {provider === 'email' ? <MagicLinkLogin /> : <PlatformLogin />}

        {/*
          Fem linjer med hvert sit pictogram frem for en punktopstilling.
          Det er den eneste side, en ny bruger ser før login, så den skal
          kunne skimmes på få sekunder.
        */}
        <section className="section" aria-labelledby="hvad-kan-du">
          <h2 className="section__title" id="hvad-kan-du">
            <Icon name="info" size={19} />
            Hvad kan du i appen?
          </h2>
          <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {LANDING_POINTS.map((point) => (
              <li key={point.text} className="meta__item">
                <Icon name={point.icon} size={18} />
                <span>{point.text}</span>
              </li>
            ))}
          </ul>
          <p className="tiny muted">
            Indmeldelse, kontaktoplysninger og kontingent håndteres fortsat i Conventus.
          </p>
        </section>
      </main>
    </div>
  );
}

/**
 * Bånd øverst på alle sider i en demo.
 *
 * En demo, der ligner den rigtige app til forveksling, er farlig: nogen vil
 * før eller siden tro, at «Test Forælder Andersen» er et rigtigt medlem, og
 * at en aflysning her når ud til rigtige familier. Båndet står derfor på
 * hver eneste side og kan ikke lukkes.
 *
 * MEN DET FYLDTE FOR MEGET. Tre linjer på hver eneste side åd en ottendedel
 * af telefonskærmen, før der stod noget, man var kommet efter. Et bånd, der
 * fylder så meget, holder desuden op med at blive læst — det bliver en del
 * af tapetet.
 *
 * Nu står den ene sætning, der faktisk betyder noget, på én linje. Resten
 * ligger bag pictogrammet og foldes ud af den, der vil vide mere. Advarslen
 * er stadig på hver side og kan stadig ikke lukkes; den råber bare ikke.
 */
function DemoBanner(): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="demo-banner">
      <p className="demo-banner__line" role="status">
        <strong>Demo</strong>
        <span>Der sendes ikke beskeder til rigtige medlemmer</span>
        <button
          type="button"
          className="demo-banner__more"
          aria-expanded={open}
          aria-controls="demo-forklaring"
          onClick={() => setOpen((v) => !v)}
        >
          <Icon name="info" size={16} />
          <span className="sr-only">
            {open ? 'Skjul forklaringen om demoen' : 'Vis hele forklaringen om demoen'}
          </span>
        </button>
      </p>
      {/*
        Teksten sagde før, at ALT var opdigtet. Da de rigtige hold blev hentet
        fra Conventus, holdt det op med at passe — og et banner, der påstår
        noget forkert om data, er værre end intet banner.
      */}
      <p className="demo-banner__full" id="demo-forklaring" hidden={!open}>
        Hold og medlemmer kan være hentet fra Conventus og kan altså være
        rigtige. Sæson, beskeder og testkonti er opdigtede. Uanset hvad du
        trykker på her, når der ingen mail og ingen notifikation ud til et
        rigtigt medlem.
      </p>
    </div>
  );
}

/**
 * App-headeren.
 *
 * Den står på hver side og gør to ting: viser hvis app det er, og giver en
 * genvej til profilen.
 *
 * INGEN KLOKKE MED ULÆSTE. Designguiden nævner en notifikation som en
 * mulighed, men bundmenuen bærer allerede antallet af ulæste på
 * Beskeder-punktet, og det punkt er synligt på hver eneste side. To
 * mærkater med det samme tal er ikke to påmindelser — det er én, man holder
 * op med at tro på.
 *
 * PROFILGENVEJEN ER TIL GENGÆLD NY OG NØDVENDIG. Profil er flyttet ind
 * under «Mere» i bundmenuen for at komme ned på fem punkter, og dermed er
 * den to tryk væk. Her er den ét.
 *
 * SIDETITLEN STÅR HELLER IKKE HER. Hver side skriver den allerede som sin
 * egen overskrift, og to ens overskrifter over hinanden er ikke et
 * hierarki — det er støj på den skærm, hvor der er mindst plads.
 */
function AppHeader({ pathname }: { pathname: string }): JSX.Element {
  return (
    <header className="appbar">
      <Link className="appbar__brand" to="/" aria-label="Skals GF — til forsiden">
        <img className="appbar__logo" src="/sgf-logo.png" alt="" width={30} height={30} />
        <span className="appbar__word">Skals GF</span>
      </Link>
      <Link
        className="appbar__action"
        to="/profil"
        aria-current={pathname === '/profil' ? 'page' : undefined}
      >
        <Icon name="profile" size={21} />
        <span className="sr-only">Profil</span>
      </Link>
    </header>
  );
}

/**
 * Login med magic link.
 *
 * HVORFOR INGEN ADGANGSKODE
 * Mailadressen er i forvejen den nøgle, medlemsregisteret matcher på. Logger
 * man ind PÅ mailen, er familietilknytningen bekræftet i samme skridt — og
 * der er ingen kode at glemme, gemme forkert eller genbruge fra et andet
 * websted.
 *
 * Svaret er altid det samme, uanset om adressen er kendt. Ellers kunne
 * formularen bruges til at afgøre, hvem der er medlem af foreningen.
 */
function MagicLinkLogin(): JSX.Element {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Et udløbet eller allerede brugt link sender brugeren hertil. Han skal
  // forstå hvorfor, i stedet for bare at se loginskærmen igen.
  const expired =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('login') === 'udloebet';

  const submit = (event: { preventDefault(): void }): void => {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    api
      .post<{ message: string; devLink?: string }>('/api/login/request', {
        email,
        returnTo: '/',
      })
      .then((result) => {
        setSent(result.message);
        setDevLink(result.devLink ?? null);
      })
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError
            ? err.message
            : 'Login-linket kunne ikke sendes. Prøv igen om et øjeblik.',
        );
      })
      .finally(() => setPending(false));
  };

  if (sent) {
    return (
      <section className="card">
        <h2>Tjek din mail</h2>
        <div className="mt-3">
          <Notice kind="success">{sent}</Notice>
        </div>
        <p className="small muted mt-3">
          Linket virker i 15 minutter og kan kun bruges én gang. Åbn det på den enhed, du vil
          bruge appen på.
        </p>
        {devLink ? (
          <p className="small mt-3">
            <a className="btn btn--secondary btn--full" href={devLink}>
              Åbn login-linket (kun i udvikling)
            </a>
          </p>
        ) : null}
        <p className="mt-3">
          <button
            type="button"
            className="btn btn--secondary btn--full"
            onClick={() => {
              setSent(null);
              setDevLink(null);
            }}
          >
            Brug en anden mailadresse
          </button>
        </p>
      </section>
    );
  }

  return (
    <section className="card">
      <h2>Log ind</h2>
      <p className="small muted mt-2">
        Skriv den mailadresse, foreningen kender dig på. Vi sender et link, du trykker på — der
        er ingen adgangskode at huske.
      </p>

      {expired ? (
        <div className="mt-3">
          <Notice kind="warning">
            Login-linket var udløbet eller allerede brugt. Bed om et nyt herunder — det tager et
            øjeblik.
          </Notice>
        </div>
      ) : null}

      {error ? (
        <div className="mt-3">
          <Notice kind="error">{error}</Notice>
        </div>
      ) : null}

      <form onSubmit={submit} className="mt-3">
        <Field label="Mailadresse" htmlFor="login-email">
          <input
            id="login-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
            placeholder="dinmail@eksempel.dk"
          />
        </Field>
        <button className="btn btn--full" type="submit" disabled={pending}>
          {pending ? 'Sender …' : 'Send mig et login-link'}
        </button>
      </form>

      <p className="tiny muted mt-3">
        Bruger foreningen en anden adresse på dig, end du plejer at bruge? Kontakt bestyrelsen —
        adressen skal rettes i Conventus.
      </p>
    </section>
  );
}

/** Login bag den platform, der leverer verificeret identitet. */
function PlatformLogin(): JSX.Element {
  return (
    <section className="card">
      <h2>Log ind for at se dine hold</h2>
      <p className="small muted mt-2">
        Appen samler holdtider, kalender, afbud, beskeder og frivillige opgaver ét sted for
        medlemmer og forældre i Skals Gymnastikforening.
      </p>
      <p className="mt-4">
        <a className="btn btn--full" href="/signin-with-chatgpt?return_to=/">
          Log ind
        </a>
      </p>
    </section>
  );
}
